const COMPANY_ROLES = [
    "ROLE_SUPER_ADMIN",
    "ROLE_COMPANY_ADMIN",
    "ROLE_COMPANY_ACCOUNTANT",
    "ROLE_COMPANY_SALESPERSON",
    "ROLE_COMPANY_CASHIER",
];

const ALL_ROLES = [...COMPANY_ROLES, "ROLE_ACCOUNT_ADMIN", "ROLE_ACCOUNT_ACCOUNTANT"];

// Carisiz kartla tahsilat (anlık, fatura bilgisiyle) plasiyere kapalı.
const ACCOUNTLESS_COLLECTION_ROLES = ALL_ROLES.filter((r) => r !== "ROLE_COMPANY_SALESPERSON");

export const GROUP_PAGE = "page";
export const GROUP_ACTION = "action";

export const GROUP_LABELS = {
    [GROUP_PAGE]: "Sayfalar",
    [GROUP_ACTION]: "Eylemler",
};

/** Türkçe'ye duyarlı normalize: "İADE" ve "iade" aynı, "ç/ğ/ı/ö/ş/ü" sade harfe iner. */
export const normalize = (value) =>
    (value || "")
        .toLocaleLowerCase("tr")
        .replace(/ı/g, "i")
        .replace(/ç/g, "c")
        .replace(/ğ/g, "g")
        .replace(/ö/g, "o")
        .replace(/ş/g, "s")
        .replace(/ü/g, "u")
        .replace(/\s+/g, " ")
        .trim();

/** Sağlayıcı adıyla arananlar doğrudan o sağlayıcının POS Rapor sayfasına gider. */
const POS_PROVIDERS = [
    { code: "PAYTR", name: "PayTR", keywords: ["paytr", "pay tr"] },
    { code: "ISYERIM_POS", name: "İşyerimPOS", keywords: ["işyerimpos", "işyerim pos", "işyerim"] },
    { code: "GARANTI", name: "Garanti BBVA", keywords: ["garanti", "garanti bbva", "bbva", "garanti bankası"] },
    { code: "QNB_PAYFOR", name: "QNB PayFor", keywords: ["qnb", "qnb payfor", "payfor", "finansbank", "qnb finansbank"] },
    { code: "ESNEK_POS", name: "Hoppa", keywords: ["hoppa", "esnek pos", "esnekpos"] },
];

export const buildSearchTargets = ({ isSuperAdmin = false, isAccountUser = false } = {}) => {
    const payment = isAccountUser ? "Ödeme" : "Tahsilat";
    const counterparty = isSuperAdmin ? "Firma" : "Cari/Bayi";

    return [
        {
            id: "dashboard",
            label: "Kontrol Paneli",
            path: "/dashboard",
            icon: "home",
            group: GROUP_PAGE,
            roles: [],
            keywords: ["anasayfa", "ana sayfa", "panel", "dashboard", "özet", "grafik", "istatistik"],
            suggested: true,
        },
        {
            id: "accounts",
            label: "Cariler/Bayiler",
            path: "/accounts",
            icon: "briefcase",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON"],
            keywords: ["cari", "cariler", "bayi", "bayiler", "müşteri", "müşteriler", "hesap listesi", "cari kodu", "cari grubu", "bakiye", "borç bakiyesi", "cari ekstre"],
            suggested: true,
        },
        {
            id: "payment-instant",
            label: `Anlık ${payment}`,
            path: "/payment?type=normal",
            icon: "credit-card",
            group: GROUP_PAGE,
            roles: ACCOUNTLESS_COLLECTION_ROLES,
            keywords: ["tahsilat", "ödeme", "kart", "çekim", "satış", "pos", "anlık"],
            suggested: true,
        },
        {
            id: "payment-account",
            label: `${counterparty} ile ${payment}`,
            path: "/payment?type=account",
            icon: "credit-card",
            group: GROUP_PAGE,
            roles: COMPANY_ROLES,
            keywords: ["tahsilat", "ödeme", "cari", "bayi", "firma", "borç kapatma"],
        },
        {
            id: "payment-invoice",
            label: `Fatura Bilgisi ile ${payment}`,
            path: "/payment?type=invoice",
            icon: "credit-card",
            group: GROUP_PAGE,
            roles: ACCOUNTLESS_COLLECTION_ROLES,
            keywords: ["tahsilat", "ödeme", "fatura", "fatura bilgisi"],
        },
        {
            id: "payment-links",
            label: `Link ile ${payment}`,
            path: "/payment-links",
            icon: "link",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            feature: "PAYMENT_LINK",
            keywords: ["link", "ödeme linki", "sms", "whatsapp", "bağlantı", "uzaktan tahsilat"],
            suggested: true,
        },
        {
            id: "payment-history",
            label: "Ödeme Geçmişi",
            path: "/payment-history",
            icon: "clock",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            keywords: ["geçmiş", "işlem", "işlemler", "makbuz", "dekont", "tahsilat listesi", "hareketler", "tahsilat geçmişi", "ödeme geçmişi", "başarılı ödeme", "başarısız ödeme", "taksitli işlem", "iade et", "iptal et", "excel"],
            suggested: true,
        },
        {
            id: "refund-history",
            label: "İade Geçmişi",
            path: "/refund-history",
            icon: "corner-up-left",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["iade", "iadeler", "geri ödeme", "refund", "para iadesi"],
        },
        {
            id: "invoice-tracking",
            label: "Vade Takip",
            path: "/invoice-tracking",
            icon: "file",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            feature: "DEBT_REMINDER",
            keywords: ["vade", "borç", "alacak", "fatura", "hatırlatma", "gecikme", "borç takip", "borç ekle", "vadesi geçen", "gecikmiş borç", "ödeme planı", "tahsilat planı", "borç hatırlatma"],
        },
        {
            id: "scheduled-reports",
            label: "Zamanlanmış Raporlar",
            path: "/schedule-reports",
            icon: "file-text",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            feature: "SCHEDULED_REPORTS",
            keywords: ["rapor", "raporlar", "excel", "zamanlanmış", "otomatik rapor", "periyodik"],
        },
        {
            id: "pos-report",
            label: "POS Rapor",
            path: "/admin/pos-raporu",
            icon: "bar-chart-2",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            feature: "POS_ANALYTICS",
            keywords: ["pos", "rapor", "analiz", "sanal pos", "komisyon", "sağlayıcı", "ciro", "net tutar", "komisyon tutarı", "banka raporu", "pos analizi"],
        },
        {
            id: "users",
            label: "Kullanıcılar",
            path: "/users",
            icon: "users",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["kullanıcı", "kullanıcılar", "personel", "yetki", "rol", "hesap"],
        },
        {
            id: "companies",
            label: "Firmalar",
            path: "/companies",
            icon: "grid",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["firma", "firmalar", "şirket", "müşteri firma", "tenant"],
        },
        {
            id: "registration-requests",
            label: "Kayıt Talepleri",
            path: "/registration-requests",
            icon: "user-check",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["kayıt", "başvuru", "talep", "onay", "yeni firma"],
        },
        {
            id: "audit-logs",
            label: "Denetim İzi",
            path: "/settings?tab=audit",
            icon: "shield",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["denetim", "log", "kayıt", "audit", "işlem geçmişi", "güvenlik", "kim ne yaptı", "işlem kaydı", "giriş kayıtları"],
        },
        {
            id: "support",
            label: "Destek Taleplerim",
            path: "/support",
            icon: "headphones",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER"],
            feature: "SUPPORT_TICKETS",
            keywords: ["destek", "yardım", "sorun", "ticket", "talep", "iletişim"],
            suggested: true,
        },
        {
            id: "support-admin",
            label: "Destek Yönetimi",
            path: "/admin/tickets",
            icon: "headphones",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["destek", "yardım", "ticket", "talepler", "sorun"],
            suggested: true,
        },
        {
            id: "subscription-details",
            label: "Aboneliğim",
            path: "/subscription/details",
            icon: "package",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["abonelik", "paket", "lisans", "üyelik", "faturam", "paketim", "kalan süre", "bitiş tarihi", "yenileme", "limit", "limitler", "deneme süresi"],
        },
        {
            id: "subscription-plans",
            label: "Paketler",
            path: "/subscription/plans",
            icon: "package",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["paket", "paketler", "fiyat", "yükselt", "abonelik", "satın al", "paket değiştir", "paket düşür", "ücretsiz paket", "standart", "profesyonel"],
        },
        {
            id: "subscription-history",
            label: "Satın Alma Geçmişi",
            path: "/subscription/history",
            icon: "package",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["satın alma", "abonelik geçmişi", "fatura", "ödemelerim"],
        },
        {
            id: "referrals",
            label: "Arkadaşını Getir",
            path: "/subscription/referrals",
            icon: "gift",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["referans", "davet", "arkadaş", "ödül", "kod"],
        },
        {
            id: "admin-subscription-dashboard",
            label: "Abonelik Genel Bakış",
            path: "/admin/subscription-dashboard",
            icon: "layers",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["abonelik", "genel bakış", "gelir", "firma abonelikleri"],
        },
        {
            id: "admin-subscription-plans",
            label: "Paket Yönetimi",
            path: "/admin/subscription-plans",
            icon: "layers",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["paket", "paketler", "fiyat", "limit", "özellik"],
        },
        {
            id: "admin-coupons",
            label: "Kupon Kodları",
            path: "/admin/coupon-codes",
            icon: "tag",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["kupon", "indirim", "kod", "promosyon"],
        },
        {
            id: "admin-price-overrides",
            label: "Fiyat Müdahaleleri",
            path: "/admin/price-overrides",
            icon: "dollar-sign",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["fiyat", "özel fiyat", "indirim", "müdahale"],
        },
        {
            id: "admin-announcements",
            label: "Duyurular",
            path: "/admin/announcements",
            icon: "volume-2",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["duyuru", "bildirim", "banner", "ilan"],
        },
        {
            id: "admin-push-notifications",
            label: "Push Bildirimleri",
            path: "/admin/announcements/push",
            icon: "smartphone",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["push", "bildirim", "mobil", "duyuru"],
        },
        {
            id: "admin-referral-program",
            label: "Referans Programı",
            path: "/admin/referral-program",
            icon: "gift",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["referans", "davet", "ödül", "arkadaşını getir"],
        },
        {
            id: "market",
            label: "360 Market",
            path: "/subscription/addons",
            icon: "shopping-bag",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["market", "360 market", "ek modül", "modül", "modül satın al", "ek paket", "ek özellik", "entegrasyon satın al"],
        },
        {
            id: "subscription-compare",
            label: "Paket Karşılaştırma",
            path: "/subscription/compare",
            icon: "columns",
            group: GROUP_PAGE,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["karşılaştır", "karşılaştırma", "paket karşılaştırma", "paket farkları", "hangi paket", "özellikler"],
        },
        {
            id: "admin-addon-modules",
            label: "Ek Modüller",
            path: "/admin/addon-modules",
            icon: "box",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["ek modül", "modül", "modüller", "market", "360 market", "modül fiyatı"],
        },
        {
            id: "admin-company-addons",
            label: "Firma Modülleri",
            path: "/admin/company-addons",
            icon: "box",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["firma modülü", "firma modülleri", "modül ata", "modül tanımla", "ek modül"],
        },
        {
            id: "admin-online-users",
            label: "Çevrimiçi Kullanıcılar",
            path: "/admin/online-users",
            icon: "activity",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["çevrimiçi", "online", "aktif kullanıcılar", "kim açık", "bağlı kullanıcılar", "son hareket"],
        },
        {
            id: "settings-vpos",
            label: "Sanal POS Ayarları",
            path: "/settings?tab=payment",
            icon: "credit-card",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["pos", "sanal pos", "vpos", "ödeme sağlayıcı", "entegrasyon", "paytr", "garanti", "param", "işyerim", "qnb", "esnek", "pos ekle", "pos başvurusu", "bağlantı testi", "sunucu ip", "ip adresi", "mağaza no", "api anahtarı", "3d secure"],
        },
        {
            id: "settings-active-pos",
            label: "Aktif POS'lar",
            path: "/settings?tab=payment",
            icon: "toggle-right",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["pos", "aktif pos", "banka", "kart markası", "komisyon", "oran"],
        },
        {
            id: "settings-accounting",
            label: "Muhasebe Entegrasyonları",
            path: "/settings?tab=accounting",
            icon: "book-open",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["muhasebe", "entegrasyon", "erp", "dia", "logo", "netsis", "paraşüt", "parasut", "bizimhesap", "aktarım", "fatura entegrasyonu"],
        },
        {
            id: "settings-groups",
            label: "Taksit Grupları",
            path: "/settings?tab=groups",
            icon: "layers",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["taksit", "taksit grubu", "taksit grupları", "cari grubu", "grup", "oran", "komisyon", "vade farkı"],
        },
        {
            id: "settings-company",
            label: "Firma Bilgileri",
            path: "/settings?tab=company",
            icon: "home",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"],
            keywords: ["firma", "firma bilgileri", "firma adı", "logo", "favicon", "markalama", "unvan", "adres", "iletişim", "telefon", "e-posta", "alan adı", "domain", "vergi dairesi", "vergi numarası"],
        },
        {
            id: "settings-currency",
            label: "Döviz Kuru Ayarları",
            path: "/settings?tab=currency",
            icon: "dollar-sign",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["döviz", "döviz kuru", "döviz kuru ayarları", "kur", "kurlar", "kur ayarı", "dolar", "usd", "euro", "eur", "avro", "sterlin", "pound", "gbp", "tcmb", "merkez bankası", "otomatik kur", "manuel kur", "kur farkı", "ekleme oranı", "spread", "yabancı para"],
        },
        {
            id: "settings-contracts",
            label: "Sözleşmeler",
            path: "/settings?tab=contracts",
            icon: "file-text",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["sözleşme", "sözleşmeler", "sözleşme metni", "sözleşme ayarları", "hizmet sözleşmesi", "online tahsilat sözleşmesi", "iptal", "iade koşulları", "gizlilik", "kvkk", "kişisel veriler", "kişisel veriler politikası", "aydınlatma metni", "politika", "örnek sözleşme"],
        },
        {
            id: "settings-user",
            label: "Kullanıcı Bilgileri",
            path: "/settings?tab=password",
            icon: "lock",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            keywords: ["kullanıcı bilgileri", "kullanıcı ayarları", "şifre", "şifre değiştir", "parola", "iki adımlı doğrulama", "2fa", "telefon", "e-posta", "hesap güvenliği"],
        },
        {
            id: "settings-holidays",
            label: "Resmî Tatil Takvimi",
            path: "/settings?tab=holidays",
            icon: "calendar",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["tatil", "resmi tatil", "tatil takvimi", "bayram", "valör", "iş günü"],
        },
        {
            id: "settings",
            label: "Ayarlar",
            path: "/settings",
            icon: "settings",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            keywords: ["ayar", "ayarlar", "pos", "entegrasyon", "sms", "mail", "logo", "taksit grubu", "banka", "döviz", "sözleşme"],
        },
        {
            id: "profile",
            label: "Profilim",
            path: "/profile",
            icon: "user",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            keywords: ["profil", "hesabım", "şifre", "parola", "iki adımlı", "2fa", "kullanıcı bilgilerim"],
        },

        {
            id: "topic-fixed-link",
            label: "Sabit Link",
            hint: `Link ile ${payment} içinde`,
            path: "/payment-links",
            icon: "link-2",
            group: GROUP_PAGE,
            roles: COMPANY_ROLES,
            feature: "FIXED_PAYMENT_LINK",
            keywords: ["sabit link", "sabit ödeme linki", "kalıcı link", "tek link", "süresiz link", "qr", "qr kod", "karekod", "kısa adres"],
        },
        {
            id: "topic-valor",
            label: "Valör",
            hint: "POS Rapor içinde",
            path: "/admin/pos-raporu",
            icon: "calendar",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            feature: "POS_ANALYTICS",
            keywords: ["valör", "valör tarihi", "valör toplamı", "hesaba geçiş", "hesaba geçecek", "ne zaman yatar", "para ne zaman yatar", "ödeme günü", "hakediş"],
        },
        {
            id: "topic-company-valor",
            label: "Firma Valör Ayarı",
            hint: "Firmalar › firmayı düzenle › Valör sekmesinde",
            path: "/companies",
            icon: "calendar",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["valör", "valör ayarı", "valör günü", "valör süresi", "firma valörü", "t+1", "t+2", "kaç gün sonra yatar"],
        },
        ...POS_PROVIDERS.map((provider) => ({
            id: `pos-report-${provider.code.toLowerCase()}`,
            label: `${provider.name} POS Raporu`,
            hint: "POS Rapor içinde",
            path: `/admin/pos-raporu/${provider.code}`,
            icon: "bar-chart-2",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            feature: "POS_ANALYTICS",
            keywords: [...provider.keywords, "pos raporu", "sağlayıcı raporu", "komisyon", "ciro"],
        })),
        {
            id: "topic-reconciliation-report",
            label: "Mahsuplaşma Raporu",
            hint: "Vade Takip içinde",
            path: "/invoice-tracking",
            icon: "file-text",
            group: GROUP_PAGE,
            roles: COMPANY_ROLES,
            feature: "DEBT_REMINDER",
            keywords: ["mahsuplaşma", "mahsup", "mahsuplaşma raporu", "mahsuplaşma gönder", "mahsuplaşma indir", "cari hesap özeti", "hesap özeti", "ekstre"],
        },
        {
            id: "topic-branding",
            label: "Logo ve Favicon",
            hint: "Ayarlar › Firma Bilgileri içinde",
            path: "/settings?tab=company",
            icon: "image",
            group: GROUP_PAGE,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["logo", "logo yükle", "logo değiştir", "favicon", "sekme ikonu", "markalama", "görsel", "marka"],
        },
        {
            id: "topic-two-factor",
            label: "İki Adımlı Doğrulama (2FA)",
            hint: "Ayarlar › Kullanıcı Bilgileri içinde",
            path: "/settings?tab=password",
            icon: "shield",
            group: GROUP_PAGE,
            roles: ALL_ROLES,
            keywords: ["iki adımlı doğrulama", "2fa", "doğrulama kodu", "authenticator", "google authenticator", "totp", "hesap güvenliği", "güvenlik"],
        },

        {
            id: "action-new-account",
            label: "Yeni cari oluştur",
            path: "/accounts/create",
            icon: "plus-circle",
            group: GROUP_ACTION,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON"],
            keywords: ["yeni cari", "cari ekle", "müşteri ekle", "bayi ekle", "kayıt"],
        },
        {
            id: "action-new-user",
            label: "Yeni kullanıcı oluştur",
            path: "/users/create",
            icon: "user-plus",
            group: GROUP_ACTION,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["yeni kullanıcı", "kullanıcı ekle", "personel ekle", "yetki ver"],
        },
        {
            id: "action-new-company",
            label: "Yeni firma oluştur",
            path: "/companies/create",
            icon: "plus-square",
            group: GROUP_ACTION,
            roles: ["ROLE_SUPER_ADMIN"],
            keywords: ["yeni firma", "firma ekle", "şirket ekle"],
        },
        {
            id: "action-collect",
            label: `${payment} al`,
            path: "/payment?type=normal",
            icon: "credit-card",
            group: GROUP_ACTION,
            roles: ACCOUNTLESS_COLLECTION_ROLES,
            keywords: ["tahsilat al", "ödeme al", "kart çek", "para al", "satış yap"],
        },
        {
            id: "action-payment-link",
            label: "Ödeme linki oluştur",
            path: "/payment-links",
            icon: "link",
            group: GROUP_ACTION,
            roles: ALL_ROLES,
            feature: "PAYMENT_LINK",
            keywords: ["link oluştur", "ödeme linki", "sms gönder", "whatsapp gönder"],
        },
        {
            id: "action-schedule-report",
            label: "Rapor planla",
            path: "/schedule-reports",
            icon: "file-text",
            group: GROUP_ACTION,
            roles: ALL_ROLES,
            feature: "SCHEDULED_REPORTS",
            keywords: ["rapor planla", "otomatik rapor", "zamanlanmış rapor", "excel gönder"],
        },
        {
            id: "action-change-password",
            label: "Şifre değiştir",
            path: "/settings?tab=password",
            icon: "lock",
            group: GROUP_ACTION,
            roles: ALL_ROLES,
            keywords: ["şifre değiştir", "şifremi değiştir", "parola değiştir", "şifre yenile", "şifremi unuttum"],
        },
        {
            id: "action-add-vpos",
            label: "Sanal POS bağla",
            path: "/settings?tab=payment",
            icon: "credit-card",
            group: GROUP_ACTION,
            roles: ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN"],
            keywords: ["pos ekle", "pos bağla", "sanal pos ekle", "sanal pos tanımla", "banka ekle", "entegrasyon ekle"],
        },
        {
            id: "action-upgrade-plan",
            label: "Paket yükselt",
            path: "/subscription/plans",
            icon: "trending-up",
            group: GROUP_ACTION,
            roles: ["ROLE_COMPANY_ADMIN"],
            keywords: ["paket yükselt", "paket değiştir", "paket satın al", "abonelik yükselt", "üst paket"],
        },
        {
            id: "action-new-ticket",
            label: "Destek talebi aç",
            path: "/support",
            icon: "headphones",
            group: GROUP_ACTION,
            roles: ["ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER"],
            feature: "SUPPORT_TICKETS",
            keywords: ["destek talebi", "talep aç", "sorun bildir", "hata bildir", "yardım iste", "şikayet"],
        },
    ];
};

/** Hedefin arama metni: etiket + anahtar kelimeler, tek seferde normalize edilir. */
const searchableOf = (target) => {
    if (!target.__searchable) {
        target.__searchable = normalize([target.label, ...(target.keywords || [])].join(" "));
        target.__label = normalize(target.label);
        target.__tokens = [...new Set(target.__searchable.split(" "))];
        target.__labelTokens = target.__label.split(" ");
    }
    return target;
};

/** Komşu iki harfin yer değiştirmesini de tek hata sayan düzenleme mesafesi. */
const editDistance = (a, b) => {
    const rows = a.length + 1;
    const cols = b.length + 1;
    const d = Array.from({ length: rows }, (_, i) => [i, ...new Array(cols - 1).fill(0)]);
    for (let j = 1; j < cols; j++) d[0][j] = j;

    for (let i = 1; i < rows; i++) {
        for (let j = 1; j < cols; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
            }
        }
    }
    return d[rows - 1][cols - 1];
};

/** Kısa kelimede hata payı yok: "pos" ile "kos" aynı sayılırsa her şey eşleşir. */
const allowedTypos = (word) => (word.length >= 8 ? 2 : word.length >= 4 ? 1 : 0);

/** Kelime, hedefteki bir kelimeye ya da onun başına yazım hatasıyla uyuyor mu? */
const isTypoOf = (word, tokens) => {
    const allowed = allowedTypos(word);
    if (allowed === 0) return false;

    return tokens.some((token) => {
        if (token.length < word.length - allowed) return false;
        if (editDistance(word, token) <= allowed) return true;
        return token.length > word.length && editDistance(word, token.slice(0, word.length)) <= allowed;
    });
};

const scoreTarget = (target, queryWords, { typos = false } = {}) => {
    searchableOf(target);

    let total = 0;
    for (const word of queryWords) {
        let best = 0;
        if (target.__label === word) best = 100;
        else if (target.__label.startsWith(word)) best = 70;
        else if (target.__label.includes(` ${word}`)) best = 55;
        else if (target.__searchable.startsWith(word) || target.__searchable.includes(` ${word}`)) best = 40;
        else if (target.__searchable.includes(word)) best = 15;
        else if (typos && isTypoOf(word, target.__labelTokens)) best = 12;
        else if (typos && isTypoOf(word, target.__tokens)) best = 8;

        if (best === 0) return 0;
        total += best;
    }
    return total;
};

const rank = (targets, words, limit, options) =>
    targets
        .map((target) => ({ target, score: scoreTarget(target, words, options) }))
        .filter((row) => row.score > 0)
        .sort((a, b) => b.score - a.score || a.target.label.localeCompare(b.target.label, "tr"))
        .slice(0, limit)
        .map((row) => row.target);

/**
 * Yetkiye göre süzülmüş hedefler içinde arar. Paket dışı hedefler elenmez:
 * menüdeki davranışla aynı şekilde görünür, seçilince deneme penceresi açılır.
 * Yazım hatasına yalnız birebir sonuç çıkmadığında bakılır; doğru yazılmış
 * aramanın sonuçlarına benzer kelimeler karışmaz.
 */
export const searchTargets = (targets, query, { limit = 8 } = {}) => {
    const words = normalize(query).split(" ").filter(Boolean);
    if (words.length === 0) return [];

    const exact = rank(targets, words, limit);
    return exact.length > 0 ? exact : rank(targets, words, limit, { typos: true });
};

export const filterByRoles = (targets, roles = []) =>
    targets.filter((target) => !target.roles?.length || target.roles.some((role) => roles.includes(role)));
